// Ausgang — SAP-Fakturen (via Adapter-Naht, heute Mock) als Quelle fuer
// Ausgangsrechnungen: Beleg waehlen -> Entwurf im Portal -> Format generieren.
// Darunter die Ausgangs-Liste mit demselben Listen-Baustein wie der Eingang.

import React, { useEffect, useState } from 'react';
import { Database, Plus, FilePlus, Settings } from 'lucide-react';
import { Card, SectionTitle, money, fmtDate, btnGhost, btnPrimary } from './ui';
import { listSapBillingDocs, createOutbound } from '../api';
import InvoiceList from './InvoiceList';
import OutboundManualForm from './OutboundManualForm';
import SellerSettings from './SellerSettings';

const OutboundScreen = ({ user, isDarkMode, t, plants = [], sapConnected = null, onOpenInvoice, showSuccess, showError }) => {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [panel, setPanel] = useState(null); // null | 'manual' | 'settings'

  useEffect(() => {
    if (sapConnected === false) return;
    listSapBillingDocs(user).then(setDocs).catch((e) => showError?.(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sapConnected]);

  const textMain = isDarkMode ? 'text-white/80' : 'text-slate-700';
  const textMuted = isDarkMode ? 'text-white/45' : 'text-slate-500';

  const createDraft = async (doc) => {
    setBusy(true);
    try {
      const res = await createOutbound(user, { sapInvoiceNumber: doc.invoiceNumber, regime: 'de' });
      if (res.ok === false) { showError?.(res.error); return; }
      showSuccess?.(t('einvoicing.draftCreatedMsg'));
      setReload((n) => n + 1);
      onOpenInvoice(res.invoiceId);
    } catch (e) { showError?.(e.message); }
    finally { setBusy(false); }
  };

  if (panel === 'manual') {
    return <OutboundManualForm user={user} isDarkMode={isDarkMode} t={t} onCancel={() => setPanel(null)}
      onCreated={(id) => { setPanel(null); onOpenInvoice(id); }} showSuccess={showSuccess} showError={showError} />;
  }
  if (panel === 'settings') {
    return <SellerSettings user={user} isDarkMode={isDarkMode} t={t} onClose={() => setPanel(null)}
      showSuccess={showSuccess} showError={showError} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2 justify-end flex-wrap">
        {user?.role === 'admin' && (
          <button className={btnGhost(isDarkMode)} onClick={() => setPanel('settings')}>
            <Settings className="w-4 h-4 mr-1.5" />{t('einvoicing.settings.button')}
          </button>
        )}
        <button className={btnPrimary(isDarkMode)} onClick={() => setPanel('manual')}>
          <FilePlus className="w-4 h-4 mr-1.5" />{t('einvoicing.manual.create')}
        </button>
      </div>
      {sapConnected !== false && <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>
          <span className="flex items-center gap-2"><Database className="w-4 h-4" />{t('einvoicing.sapBillingDocs')}</span>
        </SectionTitle>
        <p className={`text-xs mb-3 ${textMuted}`}>{t('einvoicing.sapBillingDocsHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docs.map((d) => (
            <div key={d.invoiceNumber} className={`flex items-center justify-between rounded-xl p-3 border ${isDarkMode ? 'border-white/[0.12] bg-white/5' : 'border-slate-200 bg-slate-50'}`}>
              <div className="min-w-0">
                <div className={`text-sm font-medium truncate ${textMain}`}>{d.invoiceNumber} · {d.buyer}</div>
                <div className={`text-xs ${textMuted}`}>{fmtDate(d.issueDate)} · {money(d.payable, d.currency)}{d.plant ? ` · ${t('einvoicing.plant')} ${d.plant}` : ''}</div>
              </div>
              <button disabled={busy} onClick={() => createDraft(d)} className={btnGhost(isDarkMode)} title={t('einvoicing.createDraft')}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ))}
          {docs.length === 0 && <p className={`text-sm ${textMuted}`}>{t('einvoicing.loading')}</p>}
        </div>
      </Card>}

      <InvoiceList key={reload} user={user} isDarkMode={isDarkMode} t={t} direction="outbound"
        plants={plants} onOpenInvoice={onOpenInvoice} showSuccess={showSuccess} showError={showError} />
    </div>
  );
};

export default OutboundScreen;
